import { PropsWithChildren, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "~@/hoc/withAuthenticationRequired";
import { useHomeContext } from "~@/pages/home-page/context/home-context";
import { useScheduleContext } from "~@/pages/schedule-page/context/schedule-context";
import { useWebViewDetection } from "~@/hooks/useWebViewDetection";
import {
  initializeAnalytics,
  disableAnalytics,
  trackAnalyticsEvent,
  syncUserIdentity,
  updateDefaultEventProperties,
  setUserProperties,
} from "./amplitude";
import { ANALYTICS_EVENTS } from "./events";

export const AnalyticsObserver = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { user } = useAuth();
  const { cityId, tournamentId } = useHomeContext();
  const { viewMode, dateViewType, startDate } = useScheduleContext();
  const { isWebView, platform } = useWebViewDetection();

  useEffect(() => {
    // Wait until webview detection finishes (platform is null before that)
    if (platform === null) {
      return;
    }
    if (isWebView) {
      disableAnalytics();
      return;
    }
    // Initialize Amplitude once (without userId - it will be set via syncUserIdentity)
    initializeAnalytics({});
  }, [isWebView, platform]);

  useEffect(() => {
    if (isWebView || !user?.email) {
      return;
    }

    syncUserIdentity({
      userId: user.email,
      properties: {
        email: user.email,
        roles: user.roles,
        isDeactivated: user.isDeactivated,
      },
    });
  }, [isWebView, user]);

  useEffect(() => {
    if (isWebView) {
      return;
    }

    updateDefaultEventProperties({
      city_id: cityId,
      tournament_id: tournamentId,
      schedule_view_mode: viewMode,
      schedule_date_view: dateViewType,
      schedule_start_date: startDate ? startDate.toISOString() : undefined,
      platform_context: platform,
    });

    setUserProperties({
      city_id: cityId,
      tournament_id: tournamentId,
    });
  }, [cityId, dateViewType, isWebView, platform, startDate, tournamentId, viewMode]);

  useEffect(() => {
    // Skip until detection finishes, otherwise the first screen view fires before init and is lost
    if (platform === null || isWebView) {
      return;
    }

    trackAnalyticsEvent(ANALYTICS_EVENTS.SCREEN_VIEW, {
      screen_name: location.pathname,
      screen_path: `${location.pathname}${location.search}`,
    });
  }, [isWebView, platform, location.pathname, location.search]);

  useEffect(() => {
    if (isWebView || typeof window === "undefined") {
      return;
    }

    const handleOnlineStatus = () => {
      trackAnalyticsEvent(ANALYTICS_EVENTS.NETWORK_STATUS, {
        status: navigator.onLine ? "online" : "offline",
      });
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, [isWebView]);

  return <>{children}</>;
};


